/** @format */

import {useState, useMemo, useCallback, useEffect, ChangeEvent, KeyboardEvent} from 'react';

import {Box, InputAdornment} from '@mui/material';
import {Close as CloseIcon, Search as SearchIcon} from '@mui/icons-material';
import {OnChangeFn, RowSelectionState} from '@tanstack/react-table';
import {Trans, useTranslation} from 'react-i18next';

import {TextFieldWrap} from 'components/InputsNew/TextFieldWrap';
import {TableVirtualized} from 'components/Table';
import {TanStackTableData} from 'components/Table/core/types';
import {
  StyledDialog,
  StyledDialogTitle,
  StyledDialogContent,
  StyledIconButton,
  SearchContainer,
  TableContainer,
  StyledButton,
  StyledDialogActions,
  ButtonsContainer,
} from 'components/BaseSearchModal/styles';
import {buildSelectionFromIds} from 'components/BaseSearchModal';

import {CampaignOptionsInterface, CampaignSelectionMode} from '../../types';
import {campaignColumns} from '../Form/constants';
import {filterCampaignsByMask} from './helpers';
import {InfoBanner, LightTabsContainer, LightTab} from './styles';

interface CampaignsMonitoringModalProps {
  open: boolean;
  onClose: () => void;
  campaigns: (CampaignOptionsInterface & TanStackTableData)[];
  initialMode: CampaignSelectionMode;
  initialMask: string;
  initialSelectedIds: string[];
  onConfirm: (result: {mode: CampaignSelectionMode; campaignIds: string[]; mask: string}) => void;
}

type PreviewTab = 'selected' | 'rejected';

const CampaignsMonitoringModal = ({
  open,
  onClose,
  campaigns,
  initialMode,
  initialMask,
  initialSelectedIds,
  onConfirm,
}: CampaignsMonitoringModalProps) => {
  const [mode, setMode] = useState<CampaignSelectionMode>(initialMode);
  const [mask, setMask] = useState(initialMask);
  const [previewTab, setPreviewTab] = useState<PreviewTab>('selected');
  const [manualSearch, setManualSearch] = useState('');
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() =>
    buildSelectionFromIds(initialSelectedIds),
  );
  const {t} = useTranslation();

  useEffect(() => {
    if (open) {
      setMode(initialMode);
      setMask(initialMask);
      setPreviewTab('selected');
      setManualSearch('');
      setRowSelection(buildSelectionFromIds(initialSelectedIds));
    }
  }, [open, initialMode, initialMask, initialSelectedIds]);

  const handleModeChange = (newMode: CampaignSelectionMode) => {
    setMode(newMode);
    setMask('');
    setManualSearch('');
    setRowSelection(buildSelectionFromIds(initialSelectedIds));
  };

  // --- Auto mode logic ---
  const {selected, rejected} = useMemo(
    () => filterCampaignsByMask(campaigns, mask),
    [campaigns, mask],
  );
  const autoTableData = previewTab === 'selected' ? selected : rejected;
  const isAutoConfirmDisabled = selected.length === 0;

  // --- Manual mode logic ---
  const manualFilteredData = useMemo(() => {
    if (!manualSearch) return campaigns;
    const searchLower = manualSearch.toLowerCase();
    return campaigns.filter(item =>
      ['id', 'campaignName', 'accountName'].some(col => {
        const value = item[col as keyof typeof item];
        if (typeof value === 'string') return value.toLowerCase().includes(searchLower);
        if (typeof value === 'number') return value.toString().includes(searchLower);
        return false;
      }),
    );
  }, [campaigns, manualSearch]);

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter(id => rowSelection[id]),
    [rowSelection],
  );

  const filteredIds = useMemo(
    () => manualFilteredData.map(item => String(item.id || '')),
    [manualFilteredData],
  );

  const handleRowSelectionChange = useCallback<OnChangeFn<RowSelectionState>>(updaterOrValue => {
    setRowSelection(prev =>
      typeof updaterOrValue === 'function' ? updaterOrValue(prev) : updaterOrValue,
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const newSelection: RowSelectionState = {...rowSelection};
    for (const id of filteredIds) {
      newSelection[id] = true;
    }
    setRowSelection(newSelection);
  }, [filteredIds, rowSelection]);

  const handleDeselectAll = useCallback(() => {
    setRowSelection({});
  }, []);

  const manualHasChanges = useMemo(() => {
    const currentSet = new Set(selectedIds);
    const initialSet = new Set(initialSelectedIds);
    if (currentSet.size !== initialSet.size) return true;
    for (const id of currentSet) {
      if (!initialSet.has(id)) return true;
    }
    return false;
  }, [selectedIds, initialSelectedIds]);

  // --- Confirm handlers ---
  const handleConfirm = () => {
    if (mode === 'AUTO') {
      onConfirm({mode: 'AUTO', campaignIds: selected.map(c => c.id), mask});
    } else {
      onConfirm({mode: 'MANUAL', campaignIds: selectedIds, mask: ''});
    }
    onClose();
  };

  const isConfirmDisabled = mode === 'AUTO' ? isAutoConfirmDisabled : !manualHasChanges;

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false}>
      <StyledDialogTitle>
        <Trans>Выбрать кампании</Trans>
        <StyledIconButton aria-label={t('закрыть')} onClick={onClose}>
          <CloseIcon />
        </StyledIconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        <Box sx={{mt: 1}}>
          <LightTabsContainer>
            <LightTab isActive={mode === 'AUTO'} onClick={() => handleModeChange('AUTO')}>
              <Box component='span' className='material-symbols-outlined' style={{fontSize: 16}}>
                filter_alt
              </Box>
              <Trans>Автоматически</Trans>
            </LightTab>
            <LightTab isActive={mode === 'MANUAL'} onClick={() => handleModeChange('MANUAL')}>
              <Box component='span' className='material-symbols-outlined' style={{fontSize: 16}}>
                touch_app
              </Box>
              <Trans>Вручную</Trans>
            </LightTab>
          </LightTabsContainer>
        </Box>

        {mode === 'AUTO' ? (
          <>
            <InfoBanner sx={{mt: 2}}>
              <Box sx={{display: 'flex', alignItems: 'flex-start', gap: 1}}>
                <Box
                  component='span'
                  className='material-symbols-outlined'
                  style={{fontSize: 32, flexShrink: 0}}
                >
                  filter_alt
                </Box>
                <Trans>
                  В автоматическом режиме все кампании, содержащие указанный текст в названии, будут
                  попадать в мониторинг автоматически
                </Trans>
              </Box>
            </InfoBanner>

            <SearchContainer>
              <TextFieldWrap
                fullWidth
                placeholder={t('Название или ID')}
                value={mask}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setMask(e.target.value)}
                InputProps={{
                  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter' && !isConfirmDisabled) {
                      e.preventDefault();
                      handleConfirm();
                    }
                  },
                  startAdornment: (
                    <InputAdornment position='start'>
                      <Box
                        component='span'
                        className='material-symbols-outlined'
                        style={{fontSize: 16}}
                      >
                        filter_alt
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </SearchContainer>

            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 2}}>
              <Box
                sx={theme => ({
                  color: theme.palette.semantic_light?.surface.onSurfaceLight,
                  fontSize: '14px',
                })}
              >
                <Trans>Показать</Trans>
              </Box>
              <LightTabsContainer>
                <LightTab
                  isActive={previewTab === 'selected'}
                  onClick={() => setPreviewTab('selected')}
                  compact
                >
                  <Box
                    component='span'
                    className='material-symbols-outlined'
                    style={{fontSize: 16}}
                  >
                    playlist_add_check
                  </Box>
                  <Trans>Выбранные</Trans> ({selected.length})
                </LightTab>
                <LightTab
                  isActive={previewTab === 'rejected'}
                  onClick={() => setPreviewTab('rejected')}
                  compact
                >
                  <Box
                    component='span'
                    className='material-symbols-outlined'
                    style={{fontSize: 16}}
                  >
                    filter_alt_off
                  </Box>
                  <Trans>Отсеянные</Trans> ({rejected.length})
                </LightTab>
              </LightTabsContainer>
            </Box>

            <TableContainer>
              {autoTableData.length > 0 ? (
                <TableVirtualized
                  columns={campaignColumns}
                  data={autoTableData}
                  search=''
                  enableSorting
                  defaultSortBy='campaignName'
                  defaultOrderBy='asc'
                  tableHeight={350}
                  variant='light'
                />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 350,
                    color: theme => theme.palette.semantic_light?.surface.onSurfaceLight,
                    fontSize: '14px',
                  }}
                >
                  {mask.trim() ? (
                    <Trans>Ни одной кампании не выбрано</Trans>
                  ) : (
                    <Trans>Введите маску для поиска кампаний</Trans>
                  )}
                </Box>
              )}
            </TableContainer>
          </>
        ) : (
          <>
            <SearchContainer>
              <TextFieldWrap
                fullWidth
                placeholder={t('Название или ID')}
                value={manualSearch}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setManualSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </SearchContainer>

            <ButtonsContainer>
              <StyledButton onClick={handleSelectAll} disabled={manualFilteredData.length === 0}>
                {t('Выбрать показанные ({{count}})', {count: manualFilteredData.length})}
              </StyledButton>
              <StyledButton onClick={handleDeselectAll} disabled={selectedIds.length === 0}>
                {selectedIds.length > 0
                  ? t('Снять выделение ({{count}})', {count: selectedIds.length})
                  : t('Снять выделение')}
              </StyledButton>
            </ButtonsContainer>

            <TableContainer>
              <TableVirtualized
                columns={campaignColumns}
                data={manualFilteredData}
                search=''
                enableSorting
                rowSelection={rowSelection}
                setRowSelection={handleRowSelectionChange}
                tableHeight={400}
                variant='light'
              />
            </TableContainer>
          </>
        )}

        <StyledDialogActions>
          <StyledButton
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
            isSubmit={!isConfirmDisabled}
          >
            {isConfirmDisabled ? t('Выберите кампании') : t('Подтвердить')}
          </StyledButton>
        </StyledDialogActions>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default CampaignsMonitoringModal;
